/**
 * The room-image toggle — the player's switch for a pure-text session.
 *
 * @remarks
 * D89 rule 1 keeps the picture off the input surface; this keeps it optional
 * altogether. Orpheus is a parser game before it is an illustrated one, and a
 * player who wants only prose should not have to argue with the layout: turned
 * off, the plate is not rendered at all, so the transcript and the prompt take
 * back every pixel it was using.
 *
 * A TOGGLE, not a dialog — so it is a plain button carrying `aria-pressed`
 * rather than the `aria-haspopup` the Map/Journal/Voices triggers use. It
 * shares their visual family because it sits in their row, and it names its
 * own state for assistive tech rather than relying on the pressed state alone.
 *
 * @packageDocumentation
 */

/** Props for {@link RoomImageToggleComponent}. */
export interface RoomImageToggleComponentProps {
	/** Whether plates are currently shown. */
	readonly isImageryEnabled: boolean;
	/** Called with the state the player is asking for. */
	readonly onToggle: (isImageryEnabled: boolean) => void;
}

/**
 * Render the images on/off toggle.
 *
 * @param props - {@link RoomImageToggleComponentProps}
 * @returns The toggle button
 */
export const RoomImageToggleComponent = ({ isImageryEnabled, onToggle }: RoomImageToggleComponentProps) => (
	<button
		aria-label={isImageryEnabled ? 'Images on — hide room images' : 'Images off — show room images'}
		aria-pressed={isImageryEnabled}
		className="room-image-toggle__trigger"
		onClick={() => {
			onToggle(!isImageryEnabled);
		}}
		type="button"
	>
		<RoomImageIcon isEnabled={isImageryEnabled} />
		<span className="room-image-toggle__label">Images</span>
	</button>
);

/**
 * The toggle's icon: a picture frame, struck through when plates are off — the
 * state is visible without reading, which the label alone would not give.
 *
 * @param props - Whether plates are currently shown
 * @returns The icon
 */
const RoomImageIcon = ({ isEnabled }: { readonly isEnabled: boolean }) => (
	<svg
		aria-hidden="true"
		className="room-image-toggle__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<rect height="14" rx="2" width="18" x="3" y="5" />
		<path d="m3 16 5-5 4 4 3-3 6 6" />
		{isEnabled ? undefined : <path d="M4 20 20 4" />}
	</svg>
);
